import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FraudRuleBaseDTO, FraudRulesProductLinesDTO } from '@modeso/types__dgoods-cart';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import * as FraudRulesActions from '../actions/fraud-rules.actions';
import { FraudRulesService } from '../service/fraud-rules.service';

@Injectable()
export class FraudRulesEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly fraudRulesService: FraudRulesService,
        private readonly router: Router,
    ) {}

    loadFraudRules$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FraudRulesActions.loadFraudRules),
            switchMap(() =>
                this.fraudRulesService.getFraudRules().pipe(
                    map((rules: FraudRuleBaseDTO[]) => FraudRulesActions.loadFraudRulesSuccess({ rules })),
                    catchError((error: HttpErrorResponse) => of(FraudRulesActions.loadFraudRulesFailure({ error }))),
                ),
            ),
        ),
    );
    createFraudRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FraudRulesActions.createFraudRule),
            mergeMap(({ ruleData, redirectToManagePage }) =>
                this.fraudRulesService.createFraudRule(ruleData).pipe(
                    map(() => FraudRulesActions.createFraudRuleSuccess({ redirectToManagePage })),
                    tap(({ redirectToManagePage }) => {
                        if (redirectToManagePage) {
                            this.router.navigate(['/de-ch/fraud-popups']);
                        }
                    }),
                    catchError((error: HttpErrorResponse) => of(FraudRulesActions.createFraudRuleFailure({ error }))),
                ),
            ),
        ),
    );

    updateFraudRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FraudRulesActions.updateFraudRule),
            mergeMap(({ id, ruleData }) =>
                this.fraudRulesService.updateFraudRule(id, ruleData).pipe(
                    map(() => FraudRulesActions.updateFraudRuleSuccess()),
                    tap(() => {
                        this.router.navigate(['/de-ch/fraud-popups']);
                    }),
                    catchError((error: HttpErrorResponse) => of(FraudRulesActions.updateFraudRuleFailure({ error }))),
                ),
            ),
        ),
    );

    enableFraudRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FraudRulesActions.enableFraudRule),
            mergeMap(({ id }) =>
                this.fraudRulesService.enableFraudRule(id).pipe(
                    map(() => FraudRulesActions.enableFraudRuleSuccess({ id })),
                    catchError((error: HttpErrorResponse) => of(FraudRulesActions.enableFraudRuleFailure({ error }))),
                ),
            ),
        ),
    );

    disableFraudRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FraudRulesActions.disableFraudRule),
            mergeMap(({ id }) =>
                this.fraudRulesService.disableFraudRule(id).pipe(
                    map(() => FraudRulesActions.disableFraudRuleSuccess({ id })),
                    catchError((error: HttpErrorResponse) => of(FraudRulesActions.disableFraudRuleFailure({ error }))),
                ),
            ),
        ),
    );

    deleteFraudRule$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FraudRulesActions.deleteFraudRule),
            mergeMap(({ id }) =>
                this.fraudRulesService.deleteFraudRule(id).pipe(
                    map(() => FraudRulesActions.deleteFraudRuleSuccess({ id })),
                    catchError((error: HttpErrorResponse) => of(FraudRulesActions.deleteFraudRuleFailure({ error }))),
                ),
            ),
        ),
    );

    getAvailableProductLines$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FraudRulesActions.getAvailableProductLines),
            switchMap(() =>
                this.fraudRulesService.getAvailableProductLines().pipe(
                    map((productLines: FraudRulesProductLinesDTO) =>
                        FraudRulesActions.getAvailableProductLinesSuccess({ productLines }),
                    ),
                    catchError((error: HttpErrorResponse) =>
                        of(FraudRulesActions.getAvailableProductLinesFailure({ error })),
                    ),
                ),
            ),
        ),
    );
}
